// react-router components
import { Link } from "react-router-dom";

// prop-types is library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftButton from "components/SoftComponents/SoftButton";
import SoftAvatar from "components/SoftComponents/SoftAvatar";
import SoftBadge from "components/SoftComponents/SoftBadge";
import { Box, Divider, Grid, Icon } from "@mui/material";
import ProductImages from "template/layouts/ecommerce/products/product-page/components/ProductImages";
import ProductInfo from "template/layouts/ecommerce/products/product-page/components/ProductInfo";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftSelect from "components/SoftComponents/SoftSelect";

import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';

import moment from "moment";
import { getMapImage } from "utils/map";
import { useEffect, useRef } from "react";

import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import { BrokenImage, Close } from "@mui/icons-material";

import badgeDict from "modules/inspections/components/badgeDict.json";
import SprayCan from "assets/images/spray_can_white.png";
import BrokenDevice from "assets/images/broken_device_white.png";

import { useAuth0 } from "@auth0/auth0-react";
import inspectionsAPI from "modules/inspections/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSoftUIController } from "hooks/context";
import Swal from "sweetalert2";

function getStatusColour(status) {
    var colour = "secondary";

    if (status == 'review') {
        colour = "warning";
    }
    else if (status == 'inspected') {
        colour = "info";
    }
    else if (status == "unresolved") {
        colour = "error";
    }
    else if (status == "resolved") {
        colour = "success";
    }

    return colour;
}

function InspectionPreviewCard({ user, selectedInspection, setSelectedInspection }) {

    inspectionsAPI.loadToken(useAuth0());

    const [controller] = useSoftUIController();
    const { deviceLayout, userPermissions } = controller;

    const canAssignmentsAdd = userPermissions.indexOf('operations-assignments:add') >= 0;
    const canInspectionsEdit = userPermissions.indexOf('operations-inspections:edit') >= 0;
    const canAssignmentsEdit = userPermissions.indexOf('operations-assignments:edit') >= 0;
    const canInspectionsDelete = userPermissions.indexOf('operations-inspections:delete') >= 0;
    const canAssignmentsDelete = userPermissions.indexOf('operations-assignments:delete') >= 0;

    const locationContainer = useRef(null);
    const locationMap = useRef(null);
    mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

    const queryClient = useQueryClient();

    const editMutation = useMutation({
        mutationFn: (id) => inspectionsAPI.updateInspectionStatus(id),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['getAllInspectionsForUser', user]
            });
            setSelectedInspection(null);
        },
        onError: (error) => {
            console.error("Failed to update status:", error);
        }
    });

    const handleDelete = (id) => {
        Swal.fire({
            title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Are you sure?</div>',
            text: 'This item cannot be recovered once deleted.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#cb0c9f',
            cancelButtonColor: '#8392ab',
            confirmButtonText: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:16px">Yes, delete it!</div>`
        }).then((result) => {
            if (result.isConfirmed) {
                deleteMutation.mutate(id);
            }
        });
    };

    const deleteMutation = useMutation({
        mutationFn: (id) => inspectionsAPI.deleteInspection(id),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['getAllInspectionsForUser', user]
            });
            setSelectedInspection(null);
        },
        onError: (error) => {
            console.error("Failed to update status:", error);
        }
    });

    useEffect(() => {
        if (!locationContainer.current || !selectedInspection?.location) return;

        if (!locationMap.current) {
            locationMap.current = new mapboxgl.Map({
                container: locationContainer.current,
                style: 'mapbox://styles/mapbox/dark-v11',
                center: [selectedInspection.location[1], selectedInspection.location[0]], // Long, Lat
                zoom: 13,
                interactive: false,
            });

            locationMap.current.on('load', () => {
                // Optional: Add a marker
                new mapboxgl.Marker({ color: '#ff0000' })
                    .setLngLat([selectedInspection.location[1], selectedInspection.location[0]])
                    .addTo(locationMap.current);
            });
        } else {
            locationMap.current.jumpTo({
                center: [selectedInspection.location[1], selectedInspection.location[0]],
                zoom: 15,
            });
        }

        return () => {
            if (locationMap.current) {
                locationMap.current.remove();
                locationMap.current = null;
            }
        };
    }, [selectedInspection]);

    if (selectedInspection == null) {
        return null;
    }
    else {
        // console.log(selectedInspection)
        // console.log(selectedInspection.secondary_images)

        return (
            <Card sx={{ height: '100%', overflowY: 'auto' }}>
                <SoftBox display="flex" flexDirection="column" justifyContent="space-between" p={1}>
                    <SoftBox>
                        {deviceLayout == 'mobile' && (
                            <SoftBox
                                sx={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '8px',
                                    verticalAlign: 'center',
                                    alignItems: 'center',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    backgroundColor: '#ddd',
                                    px: 1,
                                    py: 0.3,
                                    borderRadius: '10px'
                                }}
                                onClick={((e) => setSelectedInspection(null))}
                            >
                                <SoftTypography variant="button" verticalAlign="center">Close </SoftTypography>
                                {/* <Close fontSize="small" /> */}
                            </SoftBox>
                        )}
                        <PhotoProvider key={selectedInspection._id}>
                            <SoftBox p={deviceLayout == 'mobile' ? 0.5 : 1}>
                                <Grid container>
                                    <Grid item xxs={7}>
                                        <SoftBox mt={deviceLayout == 'mobile' ? 4 : 0}>
                                            <SoftBox mb={1} pr={5}>
                                                <SoftTypography variant={deviceLayout == "mobile" ? "h4" : "h3"} fontWeight="bold" textTransform="capitalize">
                                                    {selectedInspection.title}
                                                </SoftTypography>
                                            </SoftBox>
                                            <SoftBadge variant="contained" color={getStatusColour(selectedInspection.status)} badgeContent={selectedInspection.status} container />
                                            <SoftBox mt={2}>
                                                <SoftTypography variant="button" fontWeight="bold">
                                                    Date recorded
                                                </SoftTypography>
                                            </SoftBox>
                                            <SoftBox mb={1} ml={0.5}>
                                                <SoftTypography variant={"body4"} fontWeight="medium">
                                                    {moment(selectedInspection.createdAt).format("DD/MM/YY hh:mm a")}
                                                </SoftTypography>
                                            </SoftBox>
                                            <SoftBox mt={3} mb={1}>
                                                <SoftTypography variant="button" fontWeight="bold">
                                                    Additional Information
                                                </SoftTypography>
                                            </SoftBox>
                                            <SoftBox pl={0.5} pr={5}>
                                                <SoftTypography variant="body2" color="text">
                                                    {selectedInspection.description ? selectedInspection.description : "No additional information added."}
                                                </SoftTypography>
                                            </SoftBox>
                                        </SoftBox>
                                    </Grid>
                                    <Grid item xxs={5}>
                                        <Card sx={{ width: '100%' }}>
                                            <Box component="div" ref={locationContainer} sx={{ height: '225px' }} />
                                        </Card>
                                    </Grid>
                                </Grid>
                            </SoftBox>

                            {selectedInspection.devices.map((device, deviceIndex) => (
                                <Box key={deviceIndex}>
                                    <SoftBox p={deviceLayout == 'mobile' ? 0 : 0.5}>
                                        <Divider />
                                        {selectedInspection.devices.length > 1 && (
                                            <SoftBox mt={2}>
                                                <SoftTypography variant="subtitle1" fontWeight="bold">{!deviceIndex ? "Original Device" : "Replacement Device"}</SoftTypography>
                                            </SoftBox>
                                        )}
                                        <Grid container mt={selectedInspection.devices.length > 1 ? 1 : 3}>
                                            <Grid item xxs={12} lg={12} x3l={4}>
                                                <SoftBox>
                                                    <SoftBox ml={0.5}>
                                                        <SoftTypography variant="button" fontWeight="bold">
                                                            Device Details
                                                        </SoftTypography>
                                                    </SoftBox>
                                                    <SoftBox mt={1} ml={1}>
                                                        <SoftTypography variant="body3" color="text">
                                                            {`${selectedInspection.organisation_id == 'clearways' ? 'eMinor' : 'Name'}: ${device.name}`}
                                                        </SoftTypography>
                                                    </SoftBox>

                                                    <SoftBox ml={1}>
                                                        <SoftTypography variant="body3" color="text">
                                                            {selectedInspection.organisation_id == 'clearways' ? `Site ID: ${device.stop_id}` : `Stop ID: ${device.stop_id}`}
                                                        </SoftTypography>
                                                    </SoftBox>
                                                    <SoftBox ml={1} mb={1}>
                                                        <SoftTypography variant="body3" color="text">
                                                            Serial No: {device.serial_no}
                                                        </SoftTypography>
                                                    </SoftBox>
                                                </SoftBox>
                                            </Grid>
                                            <Grid item xxs={12} lg={6} x3l={4} mt={{ xxs: 2, x3l: 0 }}>
                                                <SoftBox mb={1} ml={0.5}>
                                                    <SoftTypography variant="button" fontWeight="bold">
                                                        Device Conditions
                                                    </SoftTypography>
                                                </SoftBox>
                                                {(device.conditions && Object.entries(device.conditions).filter((row) => row[1] === true || (row[0] == "other" && row[1] != "")).length > 0) ? (
                                                    Object.entries(device.conditions).filter((row) => row[1] === true || (row[0] == "other" && row[1] != "")).map(([key, value], conIndex) => (
                                                        <SoftBox key={conIndex} pb={0.25}>
                                                            <SoftBox pl={1} display="flex" flexDirection="row" alignItems="center">
                                                                {badgeDict[key].icon == "SprayCan" && (
                                                                    <SoftBox component="img" src={SprayCan} variant="gradient" bgColor={badgeDict[key].color} sx={{ borderRadius: '4px', width: '24px', height: '24px' }} />
                                                                )}
                                                                {badgeDict[key].icon == 'BrokenDevice' && (
                                                                    <SoftBox component="img" src={BrokenDevice} variant="gradient" bgColor={badgeDict[key].color} sx={{ borderRadius: '4px', width: '24px', height: '24px' }} />
                                                                )}
                                                                {(badgeDict[key].icon != "BrokenDevice" && badgeDict[key].icon != "SprayCan") && (
                                                                    <SoftBox variant="gradient" bgColor={badgeDict[key].color} sx={{ borderRadius: '4px', width: '24px', height: '24px' }}>
                                                                        <Icon fontSize="22px !important" sx={{ color: "#fff", fontSize: '22px', display: "flex", alignItems: "center", justifyContent: "center", width: '24px !important', height: '24px !important' }}>{badgeDict[key].icon}</Icon>
                                                                    </SoftBox>
                                                                )}
                                                                <SoftTypography pl={1} variant="subtitle2" textTransform="capitalize">{key.replace('_', ' ')}</SoftTypography>
                                                            </SoftBox>
                                                            {key == 'other' && (
                                                                <SoftBox pl={5}>
                                                                    <SoftTypography variant="caption" fontWeight="regular">{value}</SoftTypography>
                                                                </SoftBox>
                                                            )}
                                                        </SoftBox>
                                                    ))
                                                ) : (
                                                    <SoftBox ml={1}>
                                                        <SoftTypography variant="body5" color="text">
                                                            None listed.
                                                        </SoftTypography>
                                                    </SoftBox>
                                                )}
                                            </Grid>
                                            <Grid item xxs={12} lg={6} x3l={4} mt={{ xxs: 2, x3l: 0 }}>
                                                <SoftBox mb={1} ml={0.5}>
                                                    <SoftTypography variant="button" fontWeight="bold">
                                                        Technician Actions
                                                    </SoftTypography>
                                                </SoftBox>
                                                {(device.actions && Object.entries(device.actions).filter((row) => row[1] === true || (row[0] == "other" && row[1] != "")).length > 0) ? (
                                                    Object.entries(device.actions).filter((row) => row[1] === true || (row[0] == "other" && row[1] != "")).map(([key, value], actIndex) => (
                                                        <SoftBox pb={0.25} key={actIndex}>
                                                            <SoftBox pl={1} display="flex" flexDirection="row" alignItems="center">
                                                                <SoftBox variant="gradient" bgColor={'success'} sx={{ borderRadius: "4px", width: "24px", height: "24px" }}>
                                                                    <Icon fontSize="22px !important" sx={{ color: "#fff", fontSize: "22px", display: "flex", alignItems: "center", justifyContent: "center", width: "24px !important", height: "24px !important" }}>check</Icon>
                                                                </SoftBox>
                                                                <SoftTypography pl={1} variant="subtitle2" textTransform="capitalize">{key.replace('_', ' ')}</SoftTypography>
                                                            </SoftBox>
                                                            {key == 'other' && (
                                                                <SoftBox pl={5}>
                                                                    <SoftTypography variant="caption" fontWeight="regular">{value}</SoftTypography>
                                                                </SoftBox>
                                                            )}
                                                        </SoftBox>
                                                    ))
                                                ) : (
                                                    <SoftBox ml={1}>
                                                        <SoftTypography variant="body5" color="text">
                                                            None listed.
                                                        </SoftTypography>
                                                    </SoftBox>
                                                )}
                                            </Grid>
                                        </Grid>
                                        {(selectedInspection.images.length > 0 && selectedInspection.images.find((obj) => obj.uuid == device.uuid) != null) ? (
                                            <SoftBox mt={(device.conditions || device.actions) ? 3 : 1}>
                                                <SoftBox mb={1} ml={0.5}>
                                                    <SoftTypography variant="button" fontWeight="bold">
                                                        Site Images
                                                    </SoftTypography>
                                                </SoftBox>
                                                <SoftBox p={1} sx={{ backgroundColor: '#f9f9f9', borderRadius: '10px' }}>
                                                    <Grid container spacing={1}>
                                                        {selectedInspection.images.find((obj) => obj.uuid == device.uuid).urls.map((img, imgIndex) => {
                                                            if (!img) {
                                                                return (
                                                                    <Grid item key={imgIndex} xxs={4} lg={3} x3l={2}>
                                                                        <SoftBox
                                                                            width="100%"
                                                                            height="200px"
                                                                            display="flex"
                                                                            justifyContent="center"
                                                                            alignItems="center"
                                                                            sx={{
                                                                                borderRadius: '5px',
                                                                                transition: "box-shadow 0.3s ease-in-out",
                                                                                "&:hover": {
                                                                                    boxShadow: "0 0 15px 2px rgba(0, 0, 0, 0.3)",
                                                                                },
                                                                                border: '1px solid #aaa'
                                                                            }}
                                                                        >
                                                                            <BrokenImage fontSize="large" />
                                                                        </SoftBox>
                                                                    </Grid>
                                                                );
                                                            }
                                                            else {
                                                                return (
                                                                    <Grid item key={imgIndex} xxs={4} lg={3} x3l={2}>
                                                                        <PhotoView src={img}>
                                                                            <SoftBox
                                                                                component="img"
                                                                                src={img}
                                                                                width="100%"
                                                                                height="200px"
                                                                                sx={{
                                                                                    objectFit: 'cover',
                                                                                    borderRadius: '5px',
                                                                                    cursor: 'pointer',
                                                                                    transition: "box-shadow 0.3s ease-in-out",
                                                                                    "&:hover": {
                                                                                        boxShadow: "0 0 15px 2px rgba(0, 0, 0, 0.3)",
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </PhotoView>
                                                                    </Grid>
                                                                );
                                                            }
                                                        })}
                                                    </Grid>
                                                </SoftBox>
                                            </SoftBox>
                                        ) : (
                                            <SoftBox mt={(device.conditions || device.actions) ? 3 : 1}>
                                                <SoftBox mb={1} ml={0.5}>
                                                    <SoftTypography variant="button" fontWeight="bold">
                                                        Site Images
                                                    </SoftTypography>
                                                </SoftBox>
                                                <SoftBox p={1} sx={{ backgroundColor: '#f9f9f9', borderRadius: '10px' }}>
                                                    <SoftTypography variant="body3" fontWeight="regular">
                                                        No site images to show.
                                                    </SoftTypography>
                                                </SoftBox>
                                            </SoftBox>
                                        )}
                                    </SoftBox>
                                </Box>
                            ))}
                        </PhotoProvider>
                        {(selectedInspection.rectification != null || selectedInspection.inspection != null) && (
                            <PhotoProvider key={selectedInspection?.rectification?._id || selectedInspection?.rectification?._id}>
                                <SoftBox mt={4} py={1} px={deviceLayout == 'mobile' ? 1 : 0} sx={{ backgroundColor: '#f1f1f1', borderRadius: '10px' }}>
                                    {selectedInspection.rectification != null && (
                                        <SoftBox p={1}>
                                            <Grid container>
                                                <Grid item xxs={12}>
                                                    <SoftBox mt={deviceLayout == 'mobile' ? 1 : 0} px={deviceLayout == 'mobile' ? 0 : 1}>
                                                        <SoftBox mb={1} pr={5}>
                                                            <SoftTypography variant={deviceLayout == "mobile" ? "h5" : "h4"} fontWeight="bold" textTransform="capitalize">
                                                                Rectification: {selectedInspection.rectification.title}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        <SoftBox mt={2}>
                                                            <SoftTypography variant="button" fontWeight="bold">
                                                                Date recorded
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        <SoftBox mb={1} ml={0.5}>
                                                            <SoftTypography variant={"body4"} fontWeight="medium">
                                                                {moment(selectedInspection.rectification.createdAt).format("DD/MM/YY hh:mm a")}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        <SoftBox mt={3} mb={1}>
                                                            <SoftTypography variant="button" fontWeight="bold">
                                                                Additional Information
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        <SoftBox pl={0.5} pr={5}>
                                                            <SoftTypography variant="body2" color="text">
                                                                {selectedInspection.rectification.description ? selectedInspection.rectification.description : "No additional information added."}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                    </SoftBox>
                                                </Grid>
                                            </Grid>
                                        </SoftBox>
                                    )}
                                    {selectedInspection.inspection != null && (
                                        <SoftBox p={1}>
                                            <Grid container>
                                                <Grid item xxs={12}>
                                                    <SoftBox mt={deviceLayout == 'mobile' ? 1 : 0} px={deviceLayout == 'mobile' ? 0 : 1}>
                                                        <SoftBox mb={1} pr={5}>
                                                            <SoftTypography variant={deviceLayout == "mobile" ? "h5" : "h4"} fontWeight="bold" textTransform="capitalize">
                                                                Original Inspection: {selectedInspection.inspection.title}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        <SoftBox mt={2}>
                                                            <SoftTypography variant="button" fontWeight="bold">
                                                                Date recorded
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        <SoftBox mb={1} ml={0.5}>
                                                            <SoftTypography variant={"body4"} fontWeight="medium">
                                                                {moment(selectedInspection.inspection.createdAt).format("DD/MM/YY hh:mm a")}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        <SoftBox mt={3} mb={1}>
                                                            <SoftTypography variant="button" fontWeight="bold">
                                                                Additional Information
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        <SoftBox pl={0.5} pr={5}>
                                                            <SoftTypography variant="body2" color="text">
                                                                {selectedInspection.inspection.description ? selectedInspection.inspection.description : "No additional information added."}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                    </SoftBox>
                                                </Grid>
                                            </Grid>
                                        </SoftBox>
                                    )}
                                    {selectedInspection.secondary_devices.map((device, deviceIndex) => (
                                        <Box key={deviceIndex}>
                                            <SoftBox p={deviceLayout == 'mobile' ? 0 : 1}>
                                                <Divider />
                                                <SoftBox mt={2} mb={0.5} ml={1}>
                                                    <SoftTypography variant="h5">
                                                        {selectedInspection.secondary_devices.length > 1 && deviceIndex == 1 ? `Replacement:` : `Original:`}
                                                    </SoftTypography>
                                                </SoftBox>
                                                <Grid container ml={0.5}>
                                                    <Grid item xxs={12} lg={12} x3l={4}>
                                                        <SoftBox ml={0.5}>
                                                            <SoftTypography variant="button" fontWeight="bold">
                                                                Device Details
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        <SoftBox mt={1} ml={1}>
                                                            <SoftTypography variant="body3" color="text">
                                                                {`${selectedInspection.organisation_id == 'clearways' ? 'eMinor' : 'Name'}: ${device.name}`}
                                                            </SoftTypography>
                                                        </SoftBox>

                                                        <SoftBox ml={1}>
                                                            <SoftTypography variant="body3" color="text">
                                                                {selectedInspection.organisation_id == 'clearways' ? `Site ID: ${device.stop_id}` : `Stop ID: ${device.stop_id}`}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        <SoftBox ml={1} mb={1}>
                                                            <SoftTypography variant="body3" color="text">
                                                                Serial No: {device.serial_no}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                    </Grid>
                                                    <Grid item xxs={12} lg={6} x3l={4} mt={{ xxs: 2, x3l: 0 }}>
                                                        <SoftBox mb={0.5} ml={0.5}>
                                                            <SoftTypography variant="button" fontWeight="bold">
                                                                Device Conditions
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        {(device.conditions && Object.entries(device.conditions).filter((row) => row[1] === true || (row[0] == "other" && row[1] != "")).length > 0) ? (
                                                            Object.entries(device.conditions).filter((row) => row[1] === true || (row[0] == "other" && row[1] != "")).map(([key, value], conIndex) => (
                                                                <SoftBox key={conIndex} pb={0.25}>
                                                                    <SoftBox pl={1} display="flex" flexDirection="row" alignItems="center">
                                                                        {badgeDict[key].icon == "SprayCan" && (
                                                                            <SoftBox component="img" src={SprayCan} variant="gradient" bgColor={badgeDict[key].color} sx={{ borderRadius: '4px', width: '24px', height: '24px' }} />
                                                                        )}
                                                                        {badgeDict[key].icon == 'BrokenDevice' && (
                                                                            <SoftBox component="img" src={BrokenDevice} variant="gradient" bgColor={badgeDict[key].color} sx={{ borderRadius: '4px', width: '24px', height: '24px' }} />
                                                                        )}
                                                                        {(badgeDict[key].icon != "BrokenDevice" && badgeDict[key].icon != "SprayCan") && (
                                                                            <SoftBox variant="gradient" bgColor={badgeDict[key].color} sx={{ borderRadius: '4px', width: '24px', height: '24px' }}>
                                                                                <Icon fontSize="22px !important" sx={{ color: "#fff", fontSize: '22px', display: "flex", alignItems: "center", justifyContent: "center", width: '24px !important', height: '24px !important' }}>{badgeDict[key].icon}</Icon>
                                                                            </SoftBox>
                                                                        )}
                                                                        <SoftTypography pl={1} variant="subtitle2" textTransform="capitalize">{key.replace('_', ' ')}</SoftTypography>
                                                                    </SoftBox>
                                                                    {key == 'other' && (
                                                                        <SoftBox pl={5}>
                                                                            <SoftTypography variant="caption" fontWeight="regular">{value}</SoftTypography>
                                                                        </SoftBox>
                                                                    )}
                                                                </SoftBox>
                                                            ))
                                                        ) : (
                                                            <SoftBox ml={1}>
                                                                <SoftTypography variant="body5" color="text">
                                                                    None listed.
                                                                </SoftTypography>
                                                            </SoftBox>
                                                        )}
                                                    </Grid>
                                                    <Grid item xxs={12} lg={6} x3l={4} mt={{ xxs: 2, x3l: 0 }}>
                                                        <SoftBox mb={0.5} ml={0.5}>
                                                            <SoftTypography variant="button" fontWeight="bold">
                                                                Technician Actions
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        {(device.actions && Object.entries(device.actions).filter((row) => row[1] === true || (row[0] == "other" && row[1] != "")).length > 0) ? (
                                                            Object.entries(device.actions).filter((row) => row[1] === true || (row[0] == "other" && row[1] != "")).map(([key, value], actIndex) => (
                                                                <SoftBox pb={0.25} key={actIndex}>
                                                                    <SoftBox pl={1} display="flex" flexDirection="row" alignItems="center">
                                                                        <SoftBox variant="gradient" bgColor={'success'} sx={{ borderRadius: "4px", width: "24px", height: "24px" }}>
                                                                            <Icon fontSize="22px !important" sx={{ color: "#fff", fontSize: "22px", display: "flex", alignItems: "center", justifyContent: "center", width: "24px !important", height: "24px !important" }}>check</Icon>
                                                                        </SoftBox>
                                                                        <SoftTypography pl={1} variant="subtitle2" textTransform="capitalize">{key.replace('_', ' ')}</SoftTypography>
                                                                    </SoftBox>
                                                                    {key == 'other' && (
                                                                        <SoftBox pl={5}>
                                                                            <SoftTypography variant="caption" fontWeight="regular">{value}</SoftTypography>
                                                                        </SoftBox>
                                                                    )}
                                                                </SoftBox>
                                                            ))
                                                        ) : (
                                                            <SoftBox ml={1}>
                                                                <SoftTypography variant="body5" color="text">
                                                                    None listed.
                                                                </SoftTypography>
                                                            </SoftBox>
                                                        )}
                                                    </Grid>

                                                </Grid>
                                            </SoftBox>
                                            {(selectedInspection.secondary_images.length > 0 && selectedInspection.secondary_images.find((obj) => obj.uuid == device.uuid) != null) && (
                                                <SoftBox px={deviceLayout == 'mobile' ? 0 : 1} mt={(device.conditions || device.actions) ? 3 : 1} pb={1}>
                                                    <SoftBox mb={1} pl={1}>
                                                        <SoftTypography variant="button" fontWeight="bold">
                                                            Site Images
                                                        </SoftTypography>
                                                    </SoftBox>
                                                    <SoftBox mx={1} sx={{ backgroundColor: '#f9f9f9', borderRadius: '10px' }}>
                                                        <Box p={1}>
                                                            <Grid container spacing={1}>
                                                                {selectedInspection.secondary_images.find((obj) => obj.uuid == device.uuid).urls.map((img, imgIndex) => (
                                                                    <Grid item key={imgIndex} xxs={4} lg={3} x3l={2}>
                                                                        <PhotoView src={img}>
                                                                            <SoftBox
                                                                                component="img"
                                                                                src={img}
                                                                                width="100%"
                                                                                height="200px"
                                                                                sx={{
                                                                                    objectFit: 'cover',
                                                                                    borderRadius: '5px',
                                                                                    cursor: 'pointer',
                                                                                    transition: "box-shadow 0.3s ease-in-out",
                                                                                    "&:hover": {
                                                                                        boxShadow: "0 0 15px 2px rgba(0, 0, 0, 0.3)",
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </PhotoView>
                                                                    </Grid>
                                                                ))}
                                                            </Grid>
                                                        </Box>
                                                    </SoftBox>
                                                </SoftBox>
                                            )}
                                        </Box>
                                    ))}
                                </SoftBox>
                            </PhotoProvider>
                        )}
                    </SoftBox>
                    <SoftBox display="flex" flexDirection="row">
                        {(canAssignmentsAdd && selectedInspection.status == "unresolved") && (
                            <SoftBox p={1}>
                                <SoftButton variant="gradient" color="info" component={Link} to={`/operations/assignments/${selectedInspection._id}`}>Complete Assignment</SoftButton>
                            </SoftBox>
                        )}
                        {(canAssignmentsEdit && canInspectionsEdit && (selectedInspection.status == "review" || selectedInspection.status == "unresolved")) && (
                            <SoftBox py={1}>
                                <SoftButton
                                    variant="gradient"
                                    color="success"
                                    onClick={() => editMutation.mutate(selectedInspection._id)}
                                    disabled={editMutation.isPending}
                                >
                                    {editMutation.isPending ? "Resolving..." : "Mark as Resolved"}
                                </SoftButton>
                            </SoftBox>
                        )}
                        {(canAssignmentsDelete && canInspectionsDelete) && (
                            <SoftBox py={1}>
                                <SoftButton
                                    variant="outlined"
                                    color="error"
                                    onClick={() => handleDelete(selectedInspection._id)}
                                    disabled={deleteMutation.isPending}
                                >
                                    {deleteMutation.isPending ? "Delete..." : "Delete"}
                                </SoftButton>
                            </SoftBox>
                        )}
                    </SoftBox>
                </SoftBox>
            </Card>
        );
    }
}

// Typechecking props for the InspectionPreviewCard
InspectionPreviewCard.propTypes = {
    user: PropTypes.object,
    selectedInspection: PropTypes.object,
    setSelectedInspection: PropTypes.func
};

export default InspectionPreviewCard;